import { useEffect, useState } from 'react';
import usePrerenderReady from '@/lib/usePrerenderReady';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '@/components/SEO';
import { useLanguage } from '@/context/LanguageContext';
import { collaboratorProfilePath } from '@/lib/collaboratorSlug';
import {
  getCollaboratorVertical,
  instituteAcademyTypes,
  isInstituteAcademyType,
  isStudioVenueType,
  studioVenueTypes,
} from '@/lib/collaboratorOrganizationTypes';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const realLookingFor = (collaborator = {}) =>
  (Array.isArray(collaborator.looking_for) ? collaborator.looking_for : [])
    .map((item) => String(item).trim())
    .filter(Boolean);
const truncateBio = (value = '', limit = 220) => {
  const clean = String(value || '')
    .replace(/\*\*/g, '')
    .replace(/__/g, '')
    .replace(/`/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!clean) return '';
  if (clean.length <= limit) return clean;
  return `${clean.slice(0, limit).replace(/[,\s.-]+$/, '')}...`;
};

const Collaborators = () => {
  const { isHindi } = useLanguage();
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  usePrerenderReady(loading);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    let mounted = true;
    const loadCollaborators = async () => {
      try {
        const response = await axios.get(`${API}/collaborators/all`);
        if (mounted) setCollaborators(response.data.collaborators || []);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadCollaborators();
    return () => {
      mounted = false;
    };
  }, []);

  const instituteCollaborators = collaborators.filter((item) => isInstituteAcademyType(item.organization_type));
  const studioCollaborators = collaborators.filter((item) => isStudioVenueType(item.organization_type));
  const filteredCollaborators = collaborators.filter((item) => {
    if (activeCategory === 'all') return true;
    if (activeCategory === 'institutes') return isInstituteAcademyType(item.organization_type);
    if (activeCategory === 'studios') return isStudioVenueType(item.organization_type);
    return getCollaboratorVertical(item.organization_type) === 'collaborators';
  });

  return (
    <div className="min-h-screen bg-gray-50 pt-28 pb-16">
      <SEO
        title="Collaborators Hiring Artists"
        description="Find production houses, music labels, theatre groups, institutions, event organizers, and clients hiring artists through Kalamanch.art."
        keywords="artist collaborators, production houses India, music labels, theatre groups, event organizers, hire performers, Kalamanch collaborators"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-[2rem] shadow-lg p-6 sm:p-8 border border-orange-100 mb-8">
          <p className="text-sm uppercase tracking-[0.25em] text-[#FF6F00] font-semibold">
            {isHindi ? 'Collaborators' : 'Collaborators'}
          </p>
          <h1 className="max-w-5xl mt-3 text-[2.2rem] sm:text-[3rem] lg:text-[4rem] font-black text-[#172033] leading-[0.98] tracking-[-0.03em]">
            {isHindi ? (
              <>
                <span className="block">KalaManch.Art ke saath</span>
                <span className="block text-[#2A3554]">kalakaron ko hire karne wale sangathan aur clients</span>
              </>
            ) : (
              <>
                <span className="block">Organizations and clients</span>
                <span className="block text-[#2A3554]">
                  hiring through{' '}
                  <span className="bg-gradient-to-r from-[#FF6F00] via-[#C451C8] to-[#00BFA5] bg-clip-text text-transparent">
                    KalaManch.Art
                  </span>
                </span>
              </>
            )}
          </h1>
          <p className="launch-copy text-gray-700 mt-4 max-w-3xl text-base sm:text-lg leading-8">
            {isHindi
              ? 'Production houses, music labels, theatre groups, institutions, event organizers aur direct clients verified profiles bana kar artists tak enquiries aur private message boards ke through pahunch sakte hain.'
              : 'Production houses, music labels, theatre groups, institutions, event organizers, and direct clients can create verified profiles and reach artists through enquiries and private message boards.'}
          </p>
        </div>

        {!loading && collaborators.length > 0 && (
          <div className="mb-8 grid gap-4 lg:grid-cols-3">
            <div className="rounded-[2rem] border border-emerald-100 bg-white p-6 shadow-lg">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-emerald-700">Institutes & Academies</p>
              <h2 className="mt-3 text-2xl font-black text-gray-950">{instituteCollaborators.length}</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600">
                Dance academies, music schools, art schools, acting institutes, photography institutes, and cultural learning centres.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {instituteAcademyTypes.slice(0, 4).map((type) => (
                  <span key={type} className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800">
                    {type}
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-[2rem] border border-fuchsia-100 bg-white p-6 shadow-lg">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-fuchsia-700">Studios & Venues</p>
              <h2 className="mt-3 text-2xl font-black text-gray-950">{studioCollaborators.length}</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600">
                Recording studios, rehearsal spaces, photography studios, art studios, auditoriums, galleries, and event venues.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                {studioVenueTypes.slice(0, 4).map((type) => (
                  <span key={type} className="rounded-full bg-fuchsia-50 px-3 py-1 text-xs font-bold text-fuchsia-800">
                    {type}
                  </span>
                ))}
              </div>
            </div>
            <div className="rounded-[2rem] border border-orange-100 bg-white p-6 shadow-lg">
              <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#FF6F00]">Why this matters</p>
              <h2 className="mt-3 text-2xl font-black text-gray-950">Discover talent, training, and spaces</h2>
              <p className="mt-2 text-sm leading-6 text-gray-600">
                Students can discover training, institutes can showcase courses, and organizers can find workshops, studios, and event-ready spaces in one place.
              </p>
            </div>
          </div>
        )}

        {!loading && collaborators.length > 0 && (
          <div className="mb-8 flex flex-wrap gap-3">
            {[
              ['all', `All Collaborators (${collaborators.length})`],
              ['institutes', `Institutes & Academies (${instituteCollaborators.length})`],
              ['studios', `Studios & Venues (${studioCollaborators.length})`],
              ['other', `Other Collaborators (${collaborators.length - instituteCollaborators.length - studioCollaborators.length})`],
            ].map(([value, label]) => {
              const active = activeCategory === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => setActiveCategory(value)}
                  className={`rounded-full px-5 py-2.5 text-sm font-extrabold transition ${
                    active
                      ? 'bg-[#FF6F00] text-white shadow-lg shadow-orange-100'
                      : 'border border-orange-200 bg-white text-gray-700 hover:border-orange-300 hover:text-[#FF6F00]'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {loading ? (
          <div className="text-gray-500">{isHindi ? 'Loading collaborators...' : 'Loading collaborators...'}</div>
        ) : collaborators.length === 0 ? (
          <div className="bg-white rounded-[2rem] shadow-lg p-8 border border-orange-100 text-gray-500">
            {isHindi ? 'No collaborators have joined yet.' : 'No collaborators have joined yet.'}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredCollaborators.map((collaborator) => {
              const lookingFor = realLookingFor(collaborator).slice(0, 4);
              const vertical = getCollaboratorVertical(collaborator.organization_type);
              const publicProfession = String(collaborator.display_profession || '').trim() || collaborator.organization_category || collaborator.organization_type || 'Collaborator';
              const badgeTone =
                vertical === 'institutes'
                  ? 'bg-emerald-50 text-emerald-800'
                  : vertical === 'studios'
                    ? 'bg-fuchsia-50 text-fuchsia-800'
                    : 'bg-slate-100 text-slate-700';
              const badgeLabel =
                vertical === 'institutes'
                  ? 'Institute / Academy'
                  : vertical === 'studios'
                    ? 'Studio / Venue'
                    : 'Collaborator';

              return (
                <div key={collaborator.id} className="flex h-full flex-col bg-white rounded-[2rem] shadow-lg border border-orange-100 p-6">
                  <div className="flex items-center gap-4 mb-5">
                    <div className="w-20 h-20 rounded-full overflow-hidden bg-white border border-orange-100 flex items-center justify-center text-3xl font-bold text-[#00BFA5]">
                      {collaborator.image_url ? (
                        <img
                          src={collaborator.image_url}
                          alt={`${collaborator.organization_name || collaborator.name} collaborator profile image on Kalamanch.art`}
                          className="w-full h-full object-contain p-1"
                        />
                      ) : (
                        (collaborator.organization_name || collaborator.name || 'C').charAt(0).toUpperCase()
                      )}
                    </div>
                    <div>
                      <h2 className="text-xl font-bold text-gray-900">{collaborator.organization_name}</h2>
                      <p className="text-sm text-[#00BFA5] font-semibold">{publicProfession}</p>
                      <p className="text-sm text-gray-500">{collaborator.city || 'India'}</p>
                    </div>
                  </div>
                  <span className={`inline-flex rounded-full px-3 py-1 text-[11px] font-black uppercase tracking-[0.18em] ${badgeTone}`}>
                    {badgeLabel}
                  </span>
                  <p className="mt-4 min-h-[110px] text-sm leading-7 text-gray-600">
                    {truncateBio(collaborator.bio || `${collaborator.organization_name} is hiring artists through KalaManch.Art.`)}
                  </p>
                  <Link to={collaboratorProfilePath(collaborator)} className="mt-2 text-sm font-bold text-[#4A148C] hover:text-[#FF6F00]">
                    More
                  </Link>
                  {lookingFor.length > 0 && (
                    <div className="mt-4 rounded-2xl bg-teal-50/70 p-3">
                      <p className="text-xs font-bold uppercase tracking-[0.18em] text-teal-700">Looking for</p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {lookingFor.map((match) => (
                          <span key={match} className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-gray-700 shadow-sm">
                            {match}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="mt-auto pt-6 flex gap-3">
                    <Link to={collaboratorProfilePath(collaborator)} className="btn-primary flex-1 text-center">
                      {isHindi ? 'View Profile' : 'View Profile'}
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Collaborators;
